import { useEffect, useRef, useState } from 'react';
import { updateReferralStatus } from '../utils/adminReferralApi';
import { fetchUsers, updateUser } from '../utils/adminUserApi';
import { fetchUserData } from '../utils/adminDataApi';
import { cancelSubscription, fetchSubscription, verifyUserSubscription } from '../utils/adminSubscriptionApi';
import { useLocation, useNavigate } from 'react-router-dom';
import Notification from '../components/Notification';
import { AlertTriangle, Gift, RefreshCcw, Users, Smartphone, X, CircleX, CircleCheck, ClipboardCopy, TriangleAlert, IndianRupee } from 'lucide-react';
import Button from '../components/elements/Button';
import Loading from '../components/elements/Loading';
import Layout from '../components/Layout';
import Input from '../components/elements/Input';
import JsonDetailsModal from '../components/JsonDetailsModal';
import ConfirmModal from '../components/ConfirmModal';
import Switch from '../components/elements/Switch';
import { toDate, toDateTimeLocal } from '../utils/globalUtils';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [jsonText, setJsonText] = useState('');
  const [pageReady, setPageReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  const [notification, setNotification] = useState({ message: '', type: 'success' });
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [metricFilter, setMetricFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const requestIdRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();

  const hasReferralCode = (user) => !!(user?.referredBy && String(user.referredBy).trim());
  const includesPlan = (user, plan) => String(user?.subscriptionType || '').toLowerCase().includes(plan.toLowerCase());

  const matchesMetricFilter = (user, metric) => {
    if (!metric) return true;

    switch (metric) {
      case 'users':
        return true;
      case 'activeUsers':
      case 'activeUserRatio':
        return !!user.isActive;
      case 'deletedUsers':
      case 'deletedUserRatio':
        return !!user.isDeleted;
      case 'nomineeAccessGranted':
      case 'nomineeAccessCoverage':
        return !!user.isNomineeAccessGranted;
      case 'subscriptions':
      case 'subscriptionPenetration':
        return !!user.subscriptionId;
      case 'verifiedSubscriptions':
      case 'verificationRate':
        return !!user.isSubscriptionVerified;
      case 'freeSubscriptions':
        return includesPlan(user, 'free');
      case 'silverSubscriptions':
        return includesPlan(user, 'silver');
      case 'goldSubscriptions':
        return includesPlan(user, 'gold');
      case 'platinumSubscriptions':
        return includesPlan(user, 'platinum');
      case 'paidSubscriptions':
      case 'paidPlanMix':
        return includesPlan(user, 'silver') || includesPlan(user, 'gold') || includesPlan(user, 'platinum');
      case 'referredUsers':
      case 'referredUserRate':
        return hasReferralCode(user);
      case 'referredVerifiedUsers':
      case 'referredVerifiedRate':
        return hasReferralCode(user) && !!user.isSubscriptionVerified;
      case 'referralAmountTotal':
        return Number(user.referralAmount || 0) > 0;
      case 'referralAmountClaimed':
      case 'referralPayoutEfficiency':
        return user.referralAmountClaimStatus === 'Paid';
      case 'referralAmountUnclaimed':
        return user.referralAmountClaimStatus === 'Unpaid';
      case 'referralAmountProcessing':
      case 'referralPayoutBacklog':
        return user.referralAmountClaimStatus === 'Processing' || user.referralAmountClaimStatus === 'Unpaid';
      default:
        return true;
    }
  };

  const metricFilterLabelMap = {
    users: 'Total Users',
    activeUsers: 'Active Users',
    activeUserRatio: 'Active User Ratio',
    deletedUsers: 'Deleted Users',
    deletedUserRatio: 'Deleted User Ratio',
    nomineeAccessGranted: 'Nominee Access',
    nomineeAccessCoverage: 'Nominee Coverage',
    subscriptions: 'With Subscription',
    subscriptionPenetration: 'Subscription Penetration',
    verifiedSubscriptions: 'Verified Subscriptions',
    verificationRate: 'Verification Rate',
    freeSubscriptions: 'Free Subscriptions',
    silverSubscriptions: 'Silver Subscriptions',
    goldSubscriptions: 'Gold Subscriptions',
    platinumSubscriptions: 'Platinum Subscriptions',
    paidSubscriptions: 'Paid Subscriptions',
    paidPlanMix: 'Paid Plan Mix',
    referredUsers: 'Referred Users',
    referredUserRate: 'Referral Conversion',
    referredVerifiedUsers: 'Referred Verified Users',
    referredVerifiedRate: 'Referred Quality',
    referralAmountTotal: 'Referral Amount Total',
    referralAmountClaimed: 'Referral Claimed',
    referralAmountUnclaimed: 'Referral Unclaimed',
    referralAmountProcessing: 'Referral Processing',
    referralPayoutEfficiency: 'Referral Payout Efficiency',
    referralPayoutBacklog: 'Payout Backlog',
  };

  const clearMetricFilter = () => {
    setMetricFilter('');
    navigate('/admin-users');
  };

  useEffect(() => {
    document.title = "Users | Eternal Locker™";
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
  }, [isLoggedIn, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const metric = params.get('metric') || '';
    setMetricFilter(metric);
    setPage(1);
  }, [location.search]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!isLoggedIn) return;
    getUsers();
  }, [page, filterStatus, metricFilter, searchTerm, isLoggedIn]);

  useEffect(() => {
      // This effect runs when the users state changes
      if (!users.length) {
        setUsersData([]);
        return;
      }

      let isCancelled = false;

      const loadPageUserDetails = async () => {
        const details = await Promise.all(users.map(async (user) => {
          let subscription = null;
          let data = [];

          if (user.subscriptionId) {
            const subscriptionRes = await fetchSubscription(user.subscriptionId);
            subscription = subscriptionRes.success ? subscriptionRes.subscription : null;
          }

          const dataRes = await fetchUserData(user.id);
          data = dataRes.success ? dataRes.data : [];

          return { userId: user.id, subscription, data };
        }));

        if (!isCancelled) {
          setUsersData(details);
        }
      };

      loadPageUserDetails();

      return () => {
        isCancelled = true;
      };

      /*
      users.forEach(async user => {
        let subscription = null;
        let data = [];
        
        if (user.subscriptionId && !usersData.find(u => u.userId === user.id)?.subscription) {
          const subscriptionRes = await fetchSubscription(user.subscriptionId);
          subscription = subscriptionRes.success ? subscriptionRes.subscription : null;
        }
  
        if (!usersData.find(u => u.userId === user.id)?.data) {
          const dataRes = await fetchUserData(user.id);
          data = dataRes.success ? dataRes.data : [];
        }
  
        if (subscription || data.length > 0) {
          setUsersData(prevData => {
            const filteredData = prevData.filter(u => u.userId !== user.id);
            return [...filteredData, { userId: user.id, subscription, data }];
          });
        }
      });
      */
    }, [users]);

  const getUsers = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setNotification({ message: '', type: 'success' });
    
    if (!isLoggedIn) {
      setNotification({ message: 'Not authenticated. Please login as admin.', type: 'error' });
      setLoading(false);
      return;
    }

    const data = await fetchUsers({
      page,
      limit,
      search: searchTerm,
      referralStatus: filterStatus,
      metric: metricFilter,
    });

    // Ignore out-of-order responses (e.g. initial unfiltered call returning late).
    if (requestId !== requestIdRef.current) {
      return;
    }

    if (data.success) {
      const pageUsers = data.users || [];
      const locallyFilteredUsers = metricFilter
        ? pageUsers.filter(user => matchesMetricFilter(user, metricFilter))
        : pageUsers;

      setUsers(pageUsers);
      setFilteredUsers(locallyFilteredUsers);
      setTotalUsers(Number(data.pagination?.total || pageUsers.length));
      setTotalPages(Number(data.pagination?.totalPages || 1));
      setPageReady(true);
    } else {
      setNotification({ message: data.message || 'Failed to fetch referrals.', type: 'error' });
    }
    setLoading(false);
  };

  const handleUpdateReferralStatus = async (referral, newStatus) => {
    setNotification({ message: '', type: 'success' });
    
    const result = await updateReferralStatus(referral.id, newStatus);
    if (result.success) {
      setNotification({ message: 'Status updated successfully.', type: 'success' });
      // Update local state
      const updateReferralInList = (prevUsers) =>
        prevUsers.map(user => {
          if (user.id === referral.referrerInfo?.id) {
            const referralAmount = referral.referralAmount || 0;
            const amountChange = referral.referralAmountClaimStatus === 'Paid' && newStatus === 'Processing' ? -referralAmount
              : referral.referralAmountClaimStatus === 'Processing' && newStatus === 'Paid' ? referralAmount
              : 0;
            return {
              ...user,
              referralAmountReceived: (user.referralAmountReceived || 0) + amountChange,
            };
          }
          if (user.id === referral.id) {
            return { ...user, referralAmountClaimStatus: newStatus };
          }
          return user;
        });
      setUsers(updateReferralInList);
      setFilteredUsers(updateReferralInList);
    } else {
      setNotification({ message: result.message || 'Failed to update status.', type: 'error' });
    }
  };

  const generateUpiUrl = (referral) => {
    if (!referral.referrerInfo?.upiId) return null;
    
    const upiId = referral.referrerInfo.upiId;
    const name = referral.referrerInfo.name;
    const amount = referral.referralAmount;
    
    return `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(name)}&tn=Eternal%20Locker%20Referral%20Reward&am=${amount}&cu=INR`;
  };

  const handlePayNow = (referral) => {
    const upiUrl = generateUpiUrl(referral);
    if (upiUrl) {
      // Try to open the UPI link
      window.location.href = upiUrl;
      
      // Also copy to clipboard as fallback
      navigator.clipboard.writeText(upiUrl).then(() => {
        setNotification({ message: 'UPI link copied to clipboard!', type: 'success' });
      }).catch(() => {
        setNotification({ message: 'UPI link: ' + upiUrl, type: 'info' });
      });
    } else {
      setNotification({ message: 'No UPI ID available for this referrer.', type: 'error' });
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'Unpaid': 'bg-gray-200 text-gray-800',
      'Processing': 'bg-yellow-200 text-yellow-800',
      'Paid': 'bg-green-200 text-green-800',
      'Failed': 'bg-red-200 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[status] || 'bg-gray-200 text-gray-800'}`}>
        {status}
      </span>
    );
  };

const handleGetSubscriptionDetails = (user) => {
    fetchSubscription(user.subscriptionId)
      .then(res => {
        if (res.success) {
          setJsonText(JSON.stringify(res.subscription, null, 2));
        } else {
          setNotification({ message: res.message || 'Failed to fetch subscription details.', type: 'error' });
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to fetch subscription details.', type: 'error' });
      });
  }

  const handleGetDataCount = (user) => {
    setJsonText(JSON.stringify(usersData.find(u => u.userId === user.id)?.data, null, 2));
  }

  const handleVerifyUserSubscription = (user) => {
    const subscriptionId = user.subscriptionId;

    verifyUserSubscription(subscriptionId)
      .then(res => {
        if (res.success) {
          setUsers(prev => prev.map(u => u.id === user.id ? { ...u, isSubscriptionVerified: true } : u));
          setFilteredUsers(prev => prev.map(u => u.id === user.id ? { ...u, isSubscriptionVerified: true } : u));
          setNotification({ message: 'Subscription verified successfully.', type: 'success' });
        } else {
          setNotification({ message: res.message || 'Failed to verify subscription.', type: 'error' });
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to verify subscription.', type: 'error' });
      });
  }

  const handleGetUserDetails = (user) => {
    setJsonText(JSON.stringify(user, null, 2));
  }

  const handleUpdateUser = async (user, colValues) => {
    updateUser(user.id, colValues)
      .then(res => {
        if (res.success) {
          setUsers((prev) => prev.map(u => u.id === user.id ? { ...u, ...colValues } : u));
          setFilteredUsers((prev) => prev.map(u => u.id === user.id ? { ...u, ...colValues } : u));
          setNotification({ message: 'User updated successfully.', type: 'success' });
        } else {
          setNotification({ message: res.message || 'Failed to update user.', type: 'error' });
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to update user.', type: 'error' });
      });
  };

  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [pendingCancelUser, setPendingCancelUser] = useState(null);

  const handleCancelSubscription = (user) => {
    setPendingCancelUser(user);
    setConfirmCancelOpen(true);
  };

  const confirmCancelSubscription = () => {
    if (!pendingCancelUser) return;
    cancelSubscription(pendingCancelUser.subscriptionId)
      .then(res => {
        if (res.success) {
          setNotification({ message: 'Subscription cancelled successfully.', type: 'success' });
          // Update users and filteredUsers table
          setUsers(prev => prev.map(u =>
            u.id === pendingCancelUser.id
              ? {
                  ...u,
                  subscription: u.subscription
                    ? { ...u.subscription, status: 'cancelled' }
                    : { status: 'cancelled' }
                }
              : u
          ));
          setFilteredUsers(prev => prev.map(u =>
            u.id === pendingCancelUser.id
              ? {
                  ...u,
                  subscription: u.subscription
                    ? { ...u.subscription, status: 'cancelled' }
                    : { status: 'cancelled' }
                }
              : u
          ));
        } else {
          setNotification({ message: res.message || 'Failed to cancel subscription.', type: 'error' });
        }
      })
      .catch(() => {
        setNotification({ message: 'Failed to cancel subscription.', type: 'error' });
      });
    setConfirmCancelOpen(false);
    setPendingCancelUser(null);
  };

  return (
    <Layout full={true}>
      <Notification message={notification.message} onClose={() => setNotification({ message: '', type: 'success' })} type={notification.type} />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Gift className="w-8 h-8 text-accent-main" />
          <h1 className="text-3xl font-bold text-primary-dark">User Management</h1>
        </div>
        <Button type="primary" onClick={getUsers} className="flex items-center gap-2">
          <RefreshCcw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <div className="mb-4 text-sm text-primary-dark/80">
        Showing <span className="font-semibold">{filteredUsers.length}</span> of <span className="font-semibold">{totalUsers}</span> users (page <span className="font-semibold">{page}</span> of <span className="font-semibold">{totalPages}</span>)
      </div>

      {/* Filters */}
      <div className="bg-primary-light rounded-lg shadow px-3 mb-6">
        {metricFilter && (
          <div className="pt-3 pb-1">
            <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-accent-main/15 text-primary-dark border border-accent-main">
              <span>
                Active metric filter: {metricFilterLabelMap[metricFilter] || metricFilter}
              </span>
              <button
                type="button"
                onClick={clearMetricFilter}
                className="font-semibold underline"
              >
                Clear
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-col md:flex-row gap-2">
          <div className="flex-1 mt-3">
            <Input
              type="text"
              placeholder="Search by email or name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {['All', 'Unpaid', 'Processing', 'Paid', 'Failed'].map(status => (
              <div key={status} className="flex items-center gap-1">
              <Button
                key={status}
                type={filterStatus === status ? 'primary' : 'primary_outline'}
                onClick={() => {
                  setFilterStatus(status);
                  setPage(1);
                }}
              >
                {status}
              </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center text-accent-main w-full h-64">
          <Loading className="text-accent-main" /> Loading users...
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12 bg-primary-light rounded-lg shadow">
          <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 text-lg">No users found</p>
        </div>
      ) : (
        <div className="rounded shadow overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-accent-main text-white border border-accent-main">
                <th className="p-2 text-left">User</th>
                <th className="p-2 text-center">Active Status</th>
                <th className="p-2 text-center">Records</th>
                <th className="p-2 text-center">Nominee Access</th>
                <th className="p-2 text-center">Subscription</th>
                <th className="p-2 text-left">Referrer</th>
                <th className="p-2 text-center">Referral Amount</th>
                <th className="p-2 text-center">Referral Status</th>
                <th className="p-2 text-center">Referral Actions</th>
                <th className="p-2 text-center">Paid</th>
              </tr>
            </thead>
            <tbody className="bg-background-light text-accent-dark">
              {filteredUsers.map(user => (
                <tr key={user.id} className={`border border-accent-main hover:bg-accent-hover/10 ${user.isDeleted ? 'bg-gray-700 text-white hover:bg-red-800' : ''}`}>
                  {/* User */}
                  <td className="p-2 cursor-pointer" onClick={() => handleGetUserDetails(user)}>
                    <div>{user.name}</div>
                    <div className="text-sm">{user.email}</div>
                  </td>
                  {/* Active Status */}
                  <td className="p-2 text-center">
                    <div className="text-xs">{toDateTimeLocal(user.lastLoginDate)}</div>
                    <div className="flex flex-col items-center gap-1">
                      <span className={`px-2 py-1 rounded text-xs ${
                        user.activeStatus.status === 'Active' ? 'bg-green-200 text-green-800' :
                        user.activeStatus.status === 'Inactive' ? 'bg-yellow-200 text-yellow-800' :
                        user.activeStatus.status === 'Nominee' ? 'bg-blue-200 text-blue-800' :
                        user.activeStatus.status === 'Deleted' ? 'bg-red-200 text-red-800' :
                              'bg-gray-200 text-gray-700'
                      }`}>
                        {user.activeStatus.detail}
                      </span>
                    </div>
                  </td>
                  {/* Records Count */}
                  <td className="p-2 text-center">
                    <Button
                      type="anchor"
                      onClick={() => handleGetDataCount(user)}
                      className="font-semibold flex items-center justify-center gap-1 mx-auto"
                    >
                      {usersData.find(u => u.userId === user.id)?.data?.reduce((acc, item) => acc + (item.count || 0), 0)}
                    </Button>
                  </td>
                  {/* Nominee Access */}
                  <td className="p-2 text-center w-24">
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={!!user.isNomineeAccessGranted}
                        onChange={() => handleUpdateUser(user, { isNomineeAccessGranted: !user.isNomineeAccessGranted })}
                      />
                    </div>
                  </td>
                  {/* Subscription Status */}
                  <td className="p-2">
                    <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">
                      <div onClick={() => handleGetSubscriptionDetails(user)} className="flex gap-1 cursor-pointer">
                        <div className="flex items-center">
                          {user.subscriptionId && usersData?.find(sub => sub.userId === user.id)?.subscription ? (
                            usersData.find(sub => sub.userId === user.id).subscription.status === "active"
                              ? <CircleCheck className="w-5 h-5 text-green-500" />
                              : <CircleX className="w-5 h-5 text-red-500" />
                          ) : !user.subscriptionId ? (
                            <TriangleAlert className="w-5 h-5 text-yellow-500" />
                          ) : (
                            <Loading />
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${user.subscriptionId === null ? 'bg-gray-200 text-gray-700' :
                            user.subscriptionType === 'FREE' ? 'bg-gray-200 text-gray-700' :
                              user.subscriptionType.includes('SILVER') ? 'bg-blue-200 text-blue-800' :
                                user.subscriptionType.includes('GOLD') ? 'bg-yellow-200 text-yellow-800' :
                                  user.subscriptionType.includes('PLATINUM') ? 'bg-purple-200 text-purple-800' :
                                    'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {user.subscriptionType || (user.subscriptionId === null ? 'Not Subscribed' : 'Unknown')}
                        </span>
                      </div>

                      {user.subscriptionId && !user.isSubscriptionVerified && (
                        <Button type='warning' size="small" title="Verify subscription" onClick={() => handleVerifyUserSubscription(user)} className={`ml-auto ${user.isDeleted ? 'hidden' : ''}`}>
                          <AlertTriangle className="w-4 h-4" />
                        </Button>
                      )}
                      {usersData?.find(sub => sub.userId === user.id)?.subscription ? (
                        usersData.find(sub => sub.userId === user.id).subscription.status === "active" ? (
                          <Button type='danger' size="small" title="Cancel subscription" onClick={() => handleCancelSubscription(user)} className={`ml-auto ${user.isDeleted ? 'hidden' : ''}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        ) : null
                      ) : null}
                    </div>
                    {user.subscriptionId && usersData?.find(sub => sub.userId === user.id)?.subscription && (
                      <div className="text-xs text-primary-dark mt-1">
                        Expires: {toDate(usersData.find(sub => sub.userId === user.id).subscription.expiryDate)}
                      </div>
                    )}
                    {user.subscriptionId && user.subscriptionId.includes('sub_') &&
                      <a href={`https://dashboard.razorpay.com/app/subscriptions/${user.subscriptionId}`} target="_blank" rel="noopener noreferrer" className="text-xs text-accent-main mt-1 inline-flex items-center gap-1">
                        View on Razorpay
                      </a>
                    }
                    </div>
                  </td>
                  {/* Referrer Info */}
                  <td className="p-2">
                    {user.referrerInfo ? (
                      <>
                      <div className="cursor-pointer" onClick={() => handleGetUserDetails(user.referrerInfo)}>
                        <div className="font-medium flex items-center px-2">
                          {user.referrerInfo.name}
                        </div>
                        <div className="text-sm px-2">{user.referrerInfo.email}</div>
                        </div>
                        {user.referrerInfo.upiId && (
                          <div className="text-xs text-green-600 font-mono">
                            
                            <Button
                              type="anchor"
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(user.referrerInfo.upiId).then(() => {
                                  setNotification({ message: 'UPI ID copied to clipboard!', type: 'success' });
                                }).catch(() => {
                                  setNotification({ message: 'UPI ID: ' + user.referrerInfo.upiId, type: 'info' });
                                });
                              }}
                              title="Copy UPI ID"
                            >
                            {user.referrerInfo.upiId} <ClipboardCopy className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400 text-sm"></span>
                    )}
                  </td>
                  {/* Referral Amount */}
                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-1 font-semibold text-green-700">
                      <IndianRupee className="w-4 h-4" />
                      {user.referralAmount || 0}
                    </div>
                  </td>
                  {/* Referral Status */}
                  <td className="p-2 text-center">
                    {user.referrerInfo && getStatusBadge(user.referralAmountClaimStatus)}
                  </td>
                  {/* Referral Actions */}
                  <td className="p-2 text-center">
                    <div className="flex gap-1 justify-center flex-wrap">
                      {user.referrerInfo && user.referralAmountClaimStatus === 'Unpaid' && user.referralAmount > 0 && (
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleUpdateReferralStatus(user, 'Processing')}
                          className="text-xs"
                        >
                          Mark Processing
                        </Button>
                      )}
                      {user.referralAmountClaimStatus === 'Processing' && (
                        <>
                          {user.referrerInfo?.upiId && (
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => handlePayNow(user)}
                              className="text-xs bg-blue-600 hover:bg-blue-700 flex items-center gap-1"
                              title="Open UPI payment link"
                            >
                              <Smartphone className="w-3 h-3" />
                              Pay Now
                            </Button>
                          )}
                          <Button
                            type="primary"
                            size="small"
                            onClick={() => handleUpdateReferralStatus(user, 'Paid')}
                            className="text-xs bg-green-600 hover:bg-green-700"
                          >
                            Mark Paid
                          </Button>
                          <Button
                            type="danger"
                            size="small"
                            onClick={() => handleUpdateReferralStatus(user, 'Failed')}
                            className="text-xs"
                          >
                            Mark Failed
                          </Button>
                        </>
                      )}
                      {(user.referralAmountClaimStatus === 'Paid' || user.referralAmountClaimStatus === 'Failed') && (
                        <Button
                          type="secondary"
                          size="small"
                          onClick={() => handleUpdateReferralStatus(user, 'Processing')}
                          className="text-xs"
                        >
                          Reprocess
                        </Button>
                      )}
                    </div>
                  </td>
                  {/* Paid Amount */}
                  <td className="p-2 text-center">
                    <div className="flex items-center justify-center gap-1 font-semibold text-green-700">
                      <IndianRupee className="w-4 h-4" />
                      {user.referralAmountReceived || 0} / {(user.referralAmountTotal || 0)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 text-center text-sm text-primary-dark">
        Showing {filteredUsers.length} of {totalUsers} users
      </div>

      <div className="mt-4 flex items-center justify-center gap-2">
        <Button
          type="secondary"
          size="small"
          disabled={page <= 1 || loading}
          onClick={() => setPage(prev => Math.max(1, prev - 1))}
        >
          Previous
        </Button>
        <span className="text-sm text-primary-dark px-2">
          Page {page} / {totalPages}
        </span>
        <Button
          type="secondary"
          size="small"
          disabled={page >= totalPages || loading}
          onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
        >
          Next
        </Button>
      </div>
      <JsonDetailsModal
        open={jsonText}
        onClose={() => setJsonText('')}
        jsonText={jsonText}
      />

      <ConfirmModal
        open={confirmCancelOpen}
        title="Cancel Subscription"
        message="Are you sure you want to cancel this user's subscription? This action cannot be undone."
        onCancel={() => { setConfirmCancelOpen(false); setPendingCancelUser(null); }}
        onConfirm={confirmCancelSubscription}
      />
    </Layout>
  );
};

export default AdminUsersPage;

